import React, { FC, useCallback, useMemo } from "react";
import { Dropdown, IJSXOption, IDropdownProps } from "@evergis/ui";
import { FeatureDc } from "@evergis/api";

import { getConfigFilter } from "../../utils/getConfigFilter";
import { getDataSource } from "../../utils/getDataSource";
import { getListOptions } from "../../utils/getListOptions";
import { useWidgetContext } from "../../../Dashboard/hooks/useWidgetContext";
import { useShownOtherItems } from "../../hooks/useShownOtherItems";
import { SelectedFilters } from "../../types";
import { WidgetFilterProps } from "./types";

export const DropdownFilter: FC<WidgetFilterProps> = ({ type, filter, config, elementConfig }) => {
  const { filters, dataSources, changeFilters } = useWidgetContext(type);

  const { filters: configFilters } = config;
  const { options } = elementConfig || {};
  const { filterName, placeholder, width, noEmptyOption } = filter.options;

  const { sliceItems } = useShownOtherItems(options);
  const configFilter = getConfigFilter(filterName, configFilters);

  const filterOptions = useMemo(() => {
    const dataSource = getDataSource(configFilter.relatedDataSource, dataSources);
    const listOptions = getListOptions(sliceItems(dataSource?.features) as FeatureDc[], filterName, configFilters);

    if (noEmptyOption === undefined || noEmptyOption === false) {
      listOptions.unshift({ text: placeholder, value: "", min: null, max: null });
    }

    return listOptions;
  }, [configFilter.relatedDataSource, configFilters, dataSources, filterName, noEmptyOption, placeholder, sliceItems]);

  const value = useMemo(
    () =>
      filters?.[filterName] !== undefined
        ? Array.isArray(filters[filterName].value)
          ? null
          : (filters[filterName].value as string)
        : (getConfigFilter(filterName, configFilters).defaultValue as string),
    [configFilters, filterName, filters],
  );

  const onChange = useCallback(
    (items: SelectedFilters) => {
      changeFilters({ [filterName]: items[0] } as SelectedFilters);
    },
    [changeFilters, filterName],
  );

  if (!configFilter) return null;

  return (
    <Dropdown
      zIndex={100}
      width={`${width ?? 288}px`}
      value={value}
      placeholder={placeholder}
      options={filterOptions as IJSXOption[]}
      onChange={onChange as unknown as IDropdownProps["onChange"]}
    />
  );
};
